import { FC, useState, useEffect, useMemo, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CodeIcon from "@mui/icons-material/Code";
import DescriptionIcon from "@mui/icons-material/Description";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import DOMPurify from "dompurify";
import {
	useCreateTreatmentPlan,
	useUpdateTreatmentPlan,
} from "../../hooks/useTreatmentPlans";
import { useRewardsEngine } from "../../hooks/useRewardsEngine";
import { useCups } from "../../hooks/useCups";
import TreatmentConfigChat, {
	DraftLabels,
} from "./components/TreatmentConfigChat";
import {
	AgentConfigDraft,
	draftToPartialTemplate,
} from "./agent/draftToTemplate";
import { mergeDraftOptions, ProposedOptions } from "./agent/draftToOptions";
import {
	TreatmentPlanRequest,
	TreatmentPlan,
	TreatmentConfigTemplate,
	TreatmentConfigOptions,
	RewardEngineOption,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";
import VisitScheduleEditor from "./visitSchedule/VisitScheduleEditor";
import PlanModeSelector, {
	AuthoringMode,
	PlanMode,
} from "./components/PlanModeSelector";
import FieldDefinitionsEditor from "./components/FieldDefinitionsEditor";
import SmartProgramConfig from "./components/SmartProgramConfig";
import { GenerateSmartProgramResponse } from "../../services/treatmentPlan.service";
import {
	SmartProgramWorkflow,
	FieldDefinition,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
import BehaviorSelector from "../../components/BehaviorSelector";
import TreatmentConfigFields from "../../components/TreatmentConfigFields";
import SegmentEditor from "../../components/SegmentEditor";
import {
	contiguityErrorMessage,
	readPlanSegments,
	validateSegmentOverrides,
	validateSegmentStructure,
} from "../../components/SegmentEditor/segmentEditor.helpers";
import {} from "../../utils/temporal.utils";
import { useUserStore } from "../../stores/user.store";

/** An empty template, as the editor starts and resets to. */
const blankTemplate = (): TreatmentConfigTemplate => ({
	name: "",
	default: false,
	segment: 0,
	range: "",
	duration: 0,
	periodLength: 1,
	weeklyVisitsPerPeriod: 0,
	cmVisitScheduleOptionIds: "",
	rewardEngineId: "",
	visitsPerPeriod: 0,
	minHoursBetweenVisits: 0,
	cmVisitSchedule: {
		cmVisitScheduleOptions: 0,
		cmVisitScheduleOptionsDays: "",
		cmVisitScheduleOptionsDaysDisplay: "",
		cutDays: [],
	},
	behavior: "",
	disenrollmentDaysThreshold: undefined,
	allowCustomProviderSite: false,
	additionalTreatmentConfigs: [],
});

const CopyToClipboardButton: FC<{ text: string }> = ({ text }) => {
	const [copied, setCopied] = useState(false);
	return (
		<Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
			<IconButton
				size="small"
				onClick={() => {
					navigator.clipboard.writeText(text);
					setCopied(true);
					setTimeout(() => setCopied(false), 2000);
				}}
			>
				{copied ? (
					<CheckIcon fontSize="small" color="success" />
				) : (
					<ContentCopyIcon fontSize="small" />
				)}
			</IconButton>
		</Tooltip>
	);
};

const SavedSmartProgramDetails: FC<{
	workflow: SmartProgramWorkflow;
	fieldDefinitions: FieldDefinition[];
	description?: string;
	generationPrompt?: string;
	previewHtml?: string;
}> = ({
	workflow,
	fieldDefinitions,
	description,
	generationPrompt,
	previewHtml,
}) => {
	const [showJson, setShowJson] = useState(false);
	const [showPrompt, setShowPrompt] = useState(false);
	const [showPreview, setShowPreview] = useState(false);

	const jsonContent = JSON.stringify({ workflow, fieldDefinitions }, null, 2);

	// Extract the auto-generated part (event catalog, instructions) by removing the user description portion
	const autoGeneratedContext =
		generationPrompt && description
			? generationPrompt.substring(
					generationPrompt.indexOf(`"${description}"`) + description.length + 3,
				)
			: "";

	return (
		<>
			<Grid item xs={12}>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Button
						size="small"
						variant={showJson ? "contained" : "outlined"}
						startIcon={<CodeIcon />}
						onClick={() => setShowJson(!showJson)}
					>
						JSON
					</Button>
					{generationPrompt && (
						<Button
							size="small"
							variant={showPrompt ? "contained" : "outlined"}
							startIcon={<DescriptionIcon />}
							onClick={() => setShowPrompt(!showPrompt)}
						>
							Prompt
						</Button>
					)}
					{previewHtml && (
						<Button
							size="small"
							variant={showPreview ? "contained" : "outlined"}
							startIcon={<AutoAwesomeIcon />}
							onClick={() => setShowPreview(!showPreview)}
						>
							Preview
						</Button>
					)}
				</Box>
			</Grid>
			<Grid item xs={12}>
				<Collapse in={showJson}>
					<Paper variant="outlined" sx={{ mb: 1 }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 2,
								py: 1,
								borderBottom: "1px solid #e0e0e0",
								bgcolor: "#f5f5f5",
							}}
						>
							<Typography variant="caption" color="text.secondary">
								Workflow JSON
							</Typography>
							<CopyToClipboardButton text={jsonContent} />
						</Box>
						<Box
							component="pre"
							sx={{
								p: 2,
								m: 0,
								overflow: "auto",
								maxHeight: 400,
								fontSize: 12,
								fontFamily: "monospace",
							}}
						>
							{jsonContent}
						</Box>
					</Paper>
				</Collapse>
				<Collapse in={showPrompt}>
					{description && (
						<Paper variant="outlined" sx={{ mb: 1 }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									px: 2,
									py: 1,
									borderBottom: "1px solid #e0e0e0",
									bgcolor: "#f5f5f5",
								}}
							>
								<Typography variant="caption" color="text.secondary">
									User Description
								</Typography>
								<CopyToClipboardButton text={description} />
							</Box>
							<Box
								component="pre"
								sx={{
									p: 2,
									m: 0,
									overflow: "auto",
									maxHeight: 200,
									fontSize: 12,
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
								}}
							>
								{description}
							</Box>
						</Paper>
					)}
					{autoGeneratedContext && (
						<Paper variant="outlined" sx={{ mb: 1, opacity: 0.7 }}>
							<Box
								sx={{
									px: 2,
									py: 1,
									borderBottom: "1px solid #e0e0e0",
									bgcolor: "#f5f5f5",
								}}
							>
								<Typography variant="caption" color="text.secondary">
									Auto-generated Context (events catalog, instructions)
								</Typography>
							</Box>
							<Box
								component="pre"
								sx={{
									p: 2,
									m: 0,
									overflow: "auto",
									maxHeight: 200,
									fontSize: 11,
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
									color: "text.secondary",
								}}
							>
								{autoGeneratedContext}
							</Box>
						</Paper>
					)}
				</Collapse>
				{previewHtml && (
					<Collapse in={showPreview}>
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								mb: 1,
								overflow: "auto",
								"& > div": { maxWidth: "100%" },
								"& h1, & h2, & h3, & h4": { margin: "8px 0" },
								"& p": { margin: "4px 0" },
								"& table": {
									width: "100%",
									borderCollapse: "collapse",
									"& th, & td": {
										border: "1px solid #e0e0e0",
										padding: "8px",
										textAlign: "left",
									},
									"& th": { backgroundColor: "#f5f5f5" },
								},
							}}
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(previewHtml, {
									ALLOWED_TAGS: [
										"div",
										"span",
										"p",
										"h1",
										"h2",
										"h3",
										"h4",
										"h5",
										"h6",
										"ul",
										"ol",
										"li",
										"table",
										"thead",
										"tbody",
										"tr",
										"th",
										"td",
										"strong",
										"em",
										"br",
										"hr",
										"code",
										"pre",
									],
									ALLOWED_ATTR: ["class", "style"],
								}),
							}}
						/>
					</Collapse>
				)}
			</Grid>
		</>
	);
};

interface TreatmentPlanFormProps {
	editingPlan: TreatmentPlan | null;
	onClose: () => void;
	/** Fired only on a successful save, which `onClose` cannot distinguish. */
	onSaved?: () => void;
	/**
	 * Fired when a plan is created without the form closing, so the parent can
	 * switch this from a create into an edit of the plan that now exists.
	 */
	onPlanCreated?: (plan: TreatmentPlan) => void;
	events?: Event[];
	eventsLoading: boolean;
}

const TreatmentPlanForm: FC<TreatmentPlanFormProps> = ({
	editingPlan,
	onClose,
	onSaved,
	onPlanCreated,
	events,
	eventsLoading,
}) => {
	const { userInfo } = useUserStore();
	const defaultAccountId =
		process.env.REACT_APP_ACCOUNT_ID || "507f1f77bcf86cd799439011";

	const isEditMode = !!editingPlan && !!editingPlan._id;

	/**
	 * How this plan is being authored, for this editing session only.
	 *
	 * Not persisted and not part of the plan: the manual form and the assistant
	 * produce the same treatment config, so which one is on screen is a property
	 * of the session rather than of the plan. A saved Smart Program opens in the
	 * assistant because that is the only way it is built.
	 */
	const [nameError, setNameError] = useState<string | null>(null);
	const [isAutoSaving, setIsAutoSaving] = useState(false);

	const [authoringMode, setAuthoringMode] = useState<AuthoringMode>(
		editingPlan?.programType === "SMART_PROGRAM" ? "ASSISTANT" : "MANUAL",
	);

	/**
	 * Both assistants are keyed to a plan id — there is nothing to hold a
	 * conversation about until the plan exists — so choosing one saves the plan
	 * first and stays on the page. Picking the manual form never saves anything:
	 * that would turn an idle click into a row in the database.
	 */
	const handlePlanModeChange = async (mode: PlanMode): Promise<void> => {
		setAuthoringMode(mode.authoringMode);
		setFormData((previous) => ({ ...previous, programType: mode.programType }));

		if (mode.authoringMode !== "ASSISTANT" || isEditMode || isAutoSaving) {
			return;
		}

		setIsAutoSaving(true);
		try {
			// persistPlan reads formData, which the setState above has not applied
			// yet, so the program type is passed explicitly rather than relied on.
			const saved = await persistPlan({ programType: mode.programType });
			if (saved) onPlanCreated?.(saved);
		} finally {
			setIsAutoSaving(false);
		}
	};

	const [formData, setFormData] = useState<TreatmentPlanRequest>({
		accountId: defaultAccountId,
		name: "",
		programType: "WEEKLY_CALENDAR",
		treatmentConfig: {},
		treatmentConfigOptions: {
			treatmentConfigTemplates: [],
			visitsPerPeriod: [],
			minHoursBetweenVisits: [],
			duration: [],
			behavior: [],
			cmVisitScheduleOptions: [],
		},
		customPlanAllowed: false,
	});

	const [newVisitPeriod, setNewVisitPeriod] = useState("");
	const [newMinHours, setNewMinHours] = useState("");
	const [newDuration, setNewDuration] = useState("");

	// Template creation state
	const [showTemplateForm, setShowTemplateForm] = useState(false);
	const [editingTemplateIndex, setEditingTemplateIndex] = useState<
		number | null
	>(null);
	const [templateData, setTemplateData] =
		useState<TreatmentConfigTemplate>(blankTemplate);

	// State for adding new cut day patterns

	const { data: rewardEngines } = useRewardsEngine(true);
	const { data: cupsData } = useCups();
	const createMutation = useCreateTreatmentPlan();
	const updateMutation = useUpdateTreatmentPlan();

	// SIMS-7151: reward engine id -> display name options, reused by the segment editor's
	// collapsed header (mirrors the per-template mapping below).
	const rewardEngineOptions: RewardEngineOption[] = useMemo(
		() =>
			rewardEngines
				?.filter((engine) => engine._id)
				.map((engine) => ({
					id: engine._id as string,
					name: `${engine.eventType} - ${engine.event}`,
				})) || [],
		[rewardEngines],
	);

	/**
	 * A saved plan's own type wins over the form's.
	 *
	 * `formData` starts on the WEEKLY_CALENDAR default and is seeded from the
	 * plan afterwards, so for one render an existing Smart Program looks like a
	 * weekly plan — long enough to mount the wrong assistant.
	 */
	const resolvedProgramType = editingPlan?.programType ?? formData.programType;

	/**
	 * The assistant's draft names a behaviour and an engine by id. Reusing the
	 * same option lists the manual form shows means the two cannot disagree
	 * about what an id is called.
	 */
	const agentLabels: DraftLabels = useMemo(
		() => ({
			behaviors: Object.fromEntries(
				(events ?? [])
					.filter((event) => event._id)
					.map((event) => [event._id as string, event.name]),
			),
			engines: Object.fromEntries(
				rewardEngineOptions.map((engine) => [engine.id, engine.name]),
			),
		}),
		[events, rewardEngineOptions],
	);

	/**
	 * Append a config the assistant produced to the plan's templates.
	 *
	 * Nothing is persisted here: the template lands in the form exactly as if it
	 * had been filled in by hand, and the clinician saves the plan. An assistant
	 * that wrote to the database directly would be a config nobody reviewed.
	 */
	/**
	 * Fold options the assistant has proposed into the plan as it proposes them.
	 *
	 * Safe to run on every turn because the merge only ever adds: a value the
	 * plan already offers is left alone. Returning `previous` unchanged when
	 * there is nothing new is what stops this looping — the state object has to
	 * be identical, not merely equal.
	 */
	const applyAgentOptions = useCallback((proposed: ProposedOptions): void => {
		setFormData((previous) => {
			const { options, additions } = mergeDraftOptions(
				proposed,
				previous.treatmentConfigOptions,
			);
			if (additions.length === 0) return previous;
			return {
				...previous,
				treatmentConfigOptions: {
					...options,
					treatmentConfigTemplates:
						previous.treatmentConfigOptions?.treatmentConfigTemplates ?? [],
				},
			};
		});
		// Reads nothing from the render — the merge happens against the state
		// updater's `previous`, so this identity can stay stable and the
		// consumer's effect fires only when the draft actually changes.
	}, []);

	/**
	 * Leave the conversation and finish the config by hand.
	 *
	 * The captured half seeds the manual editor rather than being saved as a
	 * template: a partial template in the array would be a broken one.
	 */
	const continueAgentDraftManually = (draft: AgentConfigDraft | null): void => {
		setTemplateData(
			draftToPartialTemplate(
				draft,
				formData.treatmentConfigOptions,
				blankTemplate(),
			),
		);
		setEditingTemplateIndex(null);
		setShowTemplateForm(true);
		setAuthoringMode("MANUAL");
	};

	const applyAgentDraft = ({
		options,
		template,
	}: {
		options: TreatmentConfigOptions;
		template: TreatmentConfigTemplate;
	}): void => {
		setFormData((previous) => {
			// `options` already carries the plan's lists with the assistant's
			// proposals folded in, but the templates on it came from the render
			// this was computed in — take those from `previous` so a template
			// added meanwhile is not dropped.
			const templates =
				previous.treatmentConfigOptions?.treatmentConfigTemplates ?? [];
			return {
				...previous,
				treatmentConfigOptions: {
					...options,
					treatmentConfigTemplates: [...templates, template],
				},
			};
		});
	};

	// SIMS-7151/7159: the plan's default config template supplies the effective values a segment
	// inherits when its override toggle is OFF (reward engine, behavior, visit schedule,
	// disenrollment threshold).
	const defaultTemplate =
		formData.treatmentConfigOptions?.treatmentConfigTemplates?.find(
			(t) => t.default,
		) || formData.treatmentConfigOptions?.treatmentConfigTemplates?.[0];
	const segmentTemplateDefaults = {
		rewardEngineId: defaultTemplate?.rewardEngineId,
		behavior: defaultTemplate?.behavior,
		cmVisitScheduleOptionIds: defaultTemplate?.cmVisitScheduleOptionIds,
		disenrollmentDaysThreshold: defaultTemplate?.disenrollmentDaysThreshold,
	};

	// Load editing plan data
	useEffect(() => {
		if (editingPlan) {
			setFormData({
				accountId: editingPlan.accountId,
				name: editingPlan.name || "",
				programType: editingPlan.programType || "WEEKLY_CALENDAR",
				treatmentConfig: editingPlan.treatmentConfig || {},
				treatmentConfigOptions: editingPlan.treatmentConfigOptions || {
					treatmentConfigTemplates: [],
					visitsPerPeriod: [],
					minHoursBetweenVisits: [],
					duration: [],
					behavior: [],
					cmVisitScheduleOptions: [],
				},
				smartProgram: editingPlan.smartProgram,
				customPlanAllowed: editingPlan.customPlanAllowed || false,
				// SIMS-7151: pre-populate segments from the folded read location
				// (treatmentConfig.segments); absent/empty yields [] (AC3/AC4).
				segments: readPlanSegments(editingPlan.treatmentConfig),
			});
		}
	}, [editingPlan]);

	/**
	 * Save the plan and report what was saved, WITHOUT closing the form.
	 *
	 * Split out from `handleSubmit` because choosing an assistant needs the plan
	 * to exist while the user stays on the page: the conversation is keyed to a
	 * plan id, so there is nothing to talk about until one exists.
	 *
	 * Returns null when a validation gate stopped the save, so callers can tell
	 * "refused" from "saved".
	 */
	const persistPlan = async (
		overrides: Partial<TreatmentPlanRequest> = {},
	): Promise<TreatmentPlan | null> => {
		if (!formData.name.trim()) {
			setNameError("A treatment plan needs a name.");
			return null;
		}

		try {
			// Clean the payload before sending to API
			const cleanedFormData = { ...formData, ...overrides };

			// Remove frontend-only fields from cmVisitScheduleOptions
			if (cleanedFormData.treatmentConfigOptions?.cmVisitScheduleOptions) {
				cleanedFormData.treatmentConfigOptions.cmVisitScheduleOptions =
					cleanedFormData.treatmentConfigOptions.cmVisitScheduleOptions.map(
						(option) => {
							const cleanOption = { ...option };
							if ("selectedWeekdays" in cleanOption) {
								delete (cleanOption as Record<string, unknown>)
									.selectedWeekdays;
							}
							if ("selectedCutDays" in cleanOption) {
								delete (cleanOption as Record<string, unknown>).selectedCutDays;
							}
							return cleanOption;
						},
					);
			}

			// Remove frontend-only fields from cmVisitSchedule in templates
			if (cleanedFormData.treatmentConfigOptions?.treatmentConfigTemplates) {
				cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates =
					cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates.map(
						(template) => {
							const cleanTemplate = { ...template };
							if (cleanTemplate.cmVisitSchedule) {
								const cleanSchedule = { ...cleanTemplate.cmVisitSchedule };
								if ("selectedWeekdays" in cleanSchedule) {
									delete (cleanSchedule as Record<string, unknown>)
										.selectedWeekdays;
								}
								if ("selectedCutDays" in cleanSchedule) {
									delete (cleanSchedule as Record<string, unknown>)
										.selectedCutDays;
								}
								cleanTemplate.cmVisitSchedule = cleanSchedule;
							}
							return cleanTemplate;
						},
					);
			}

			// Validate templates
			if (cleanedFormData.treatmentConfigOptions?.treatmentConfigTemplates) {
				const invalidTemplates =
					cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates.filter(
						(template) =>
							!template.name.trim() ||
							!template.range.trim() ||
							template.segment <= 0 ||
							template.duration < 0 ||
							!template.weeklyVisitsPerPeriod ||
							!template.rewardEngineId.trim() ||
							!template.cmVisitScheduleOptionIds.trim() ||
							!template.cmVisitSchedule.cmVisitScheduleOptionsDays.trim() ||
							!template.cmVisitSchedule.cmVisitScheduleOptionsDaysDisplay.trim() ||
							!template.behavior.trim(),
					);

				if (invalidTemplates.length > 0) {
					alert(
						"Please fill all required template fields before saving the treatment plan.",
					);
					return null;
				}
			}

			// SIMS-7151 (AC1): the structural fields must be usable. Checked FIRST — a segment with
			// no visits per week makes every later rule meaningless (it occupies zero visits, so it
			// silently never applies at runtime).
			const structureError = validateSegmentStructure(formData.segments || []);
			if (structureError) {
				alert(structureError);
				return null;
			}

			// SIMS-7159 (AC2): a segment override toggled ON but left empty/invalid blocks save.
			const overrideError = validateSegmentOverrides(formData.segments || []);
			if (overrideError) {
				alert(overrideError);
				return null;
			}

			// SIMS-7167 (AC2/AC3/AC5): non-contiguous / overlapping / not-starting-at-1 segment
			// week ranges block save (mirrors the server-side rule so the UI catches it first).
			const contiguityError = contiguityErrorMessage(formData.segments || []);
			if (contiguityError) {
				alert(contiguityError);
				return null;
			}

			if (isEditMode && editingPlan) {
				return await updateMutation.mutateAsync({
					id: editingPlan._id,
					data: { ...cleanedFormData, updatedBy: userInfo?.id },
				});
			}

			return await createMutation.mutateAsync({
				...cleanedFormData,
				createdBy: userInfo?.id,
			});
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("Error saving treatment plan:", err);
			return null;
		}
	};

	const handleSubmit = async (): Promise<void> => {
		const saved = await persistPlan();
		if (!saved) return;
		onSaved?.();
		onClose();
	};

	const addArrayItem = (
		field: keyof NonNullable<typeof formData.treatmentConfigOptions>,
		value: string,
		setter: (value: string) => void,
	) => {
		if (value.trim() && formData.treatmentConfigOptions) {
			const currentArray =
				(formData.treatmentConfigOptions[field] as string[]) || [];
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					[field]: [...currentArray, value.trim()],
				},
			});
			setter("");
		}
	};

	const removeArrayItem = (
		field: keyof NonNullable<typeof formData.treatmentConfigOptions>,
		index: number,
	) => {
		if (formData.treatmentConfigOptions) {
			const currentArray =
				(formData.treatmentConfigOptions[field] as string[]) || [];
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					[field]: currentArray.filter((_, i) => i !== index),
				},
			});
		}
	};

	const addTemplate = () => {
		if (!formData.treatmentConfigOptions) {
			setFormData({
				...formData,
				treatmentConfigOptions: {
					treatmentConfigTemplates: [templateData],
					visitsPerPeriod: [],
					minHoursBetweenVisits: [],
					duration: [],
					behavior: [],
				},
			});
		} else {
			const currentTemplates =
				formData.treatmentConfigOptions.treatmentConfigTemplates || [];

			// Check if we're editing an existing template
			if (editingTemplateIndex !== null) {
				// Update existing template
				const updatedTemplates = [...currentTemplates];
				updatedTemplates[editingTemplateIndex] = templateData;
				setFormData({
					...formData,
					treatmentConfigOptions: {
						...formData.treatmentConfigOptions,
						treatmentConfigTemplates: updatedTemplates,
					},
				});
			} else {
				// Add new template
				setFormData({
					...formData,
					treatmentConfigOptions: {
						...formData.treatmentConfigOptions,
						treatmentConfigTemplates: [...currentTemplates, templateData],
					},
				});
			}
		}

		// Reset form
		setTemplateData({
			name: "",
			default: false,
			segment: 0,
			range: "",
			duration: 0,
			periodLength: 1,
			weeklyVisitsPerPeriod: 0,
			cmVisitScheduleOptionIds: "",
			rewardEngineId: "",
			visitsPerPeriod: 0,
			minHoursBetweenVisits: 0,
			cmVisitSchedule: {
				cmVisitScheduleOptions: 0,
				cmVisitScheduleOptionsDays: "",
				cmVisitScheduleOptionsDaysDisplay: "",
				cutDays: [],
			},
			behavior: "",
			disenrollmentDaysThreshold: undefined,
			allowCustomProviderSite: false,
			additionalTreatmentConfigs: [],
		});
		setShowTemplateForm(false);
		setEditingTemplateIndex(null);
	};

	const removeTemplate = (index: number) => {
		if (formData.treatmentConfigOptions?.treatmentConfigTemplates) {
			const currentTemplates =
				formData.treatmentConfigOptions.treatmentConfigTemplates;
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					treatmentConfigTemplates: currentTemplates.filter(
						(_, i) => i !== index,
					),
				},
			});
		}
	};

	const editTemplate = (index: number) => {
		if (formData.treatmentConfigOptions?.treatmentConfigTemplates) {
			const template =
				formData.treatmentConfigOptions.treatmentConfigTemplates[index];
			setTemplateData(template);
			setEditingTemplateIndex(index);
			setShowTemplateForm(true);
		}
	};

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}
				>
					<Typography variant="h6">
						{isEditMode
							? "Edit Treatment Plan"
							: editingPlan
								? "Clone Treatment Plan"
								: "Create New Treatment Plan"}
					</Typography>
					<Button variant="outlined" onClick={onClose}>
						Cancel
					</Button>
				</Box>

				<Grid container spacing={3}>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Account ID"
							value={formData.accountId}
							onChange={(e) =>
								setFormData({
									...formData,
									accountId: e.target.value,
								})
							}
							placeholder="Enter account ID"
							helperText="Default account ID is pre-filled from environment"
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Treatment Plan Name"
							value={formData.name}
							onChange={(e) => {
								setNameError(null);
								setFormData({
									...formData,
									name: e.target.value,
								});
							}}
							placeholder="Enter treatment plan name"
							required
							error={!!nameError}
							helperText={
								nameError ??
								"A descriptive name to identify this treatment plan"
							}
						/>
					</Grid>

					<Grid item xs={12}>
						<PlanModeSelector
							value={{
								programType: formData.programType || "WEEKLY_CALENDAR",
								authoringMode,
							}}
							onChange={handlePlanModeChange}
							// Program type is fixed once the plan exists; authoring mode
							// never is, so this locks the type rather than the whole control.
							lockedProgramType={
								isEditMode ? editingPlan?.programType || null : null
							}
						/>
					</Grid>

					{/* The treatment-config assistant. Same output as the manual form
					    below, which stays on screen: the assistant fills the template
					    in, and the clinician reviews and saves it there. */}
					{authoringMode === "ASSISTANT" &&
						resolvedProgramType !== "SMART_PROGRAM" && (
							<Grid item xs={12}>
								{isEditMode && editingPlan ? (
									<TreatmentConfigChat
										treatmentPlanId={editingPlan._id}
										planOptions={formData.treatmentConfigOptions}
										labels={agentLabels}
										onApply={applyAgentDraft}
										onOptionsProposed={applyAgentOptions}
										onContinueManually={continueAgentDraftManually}
									/>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{
											p: 2,
											textAlign: "center",
											border: "1px dashed #ccc",
											borderRadius: 1,
										}}
									>
										{isAutoSaving
											? "Saving the plan so the assistant has something to work on…"
											: "Give the plan a name to start the assistant."}
									</Typography>
								)}
							</Grid>
						)}

					{formData.programType === "SMART_PROGRAM" && (
						<>
							<Grid item xs={12}>
								{isEditMode && editingPlan ? (
									<SmartProgramConfig
										treatmentPlanId={editingPlan._id}
										onApprove={(result: GenerateSmartProgramResponse) => {
											setFormData({
												...formData,
												smartProgram: {
													workflow:
														result.workflow as unknown as SmartProgramWorkflow,
													fieldDefinitions:
														result.fieldDefinitions as FieldDefinition[],
													description: result.description,
													generationPrompt: result.prompt,
													previewHtml: result.previewHtml,
												},
											});
										}}
										onCancel={() => {
											/* no-op */
										}}
									/>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{
											p: 2,
											textAlign: "center",
											border: "1px dashed #ccc",
											borderRadius: 1,
										}}
									>
										Save the treatment plan first, then use AI generation to
										configure the Smart Program workflow.
									</Typography>
								)}
							</Grid>
							{formData.smartProgram?.workflow && (
								<SavedSmartProgramDetails
									workflow={formData.smartProgram.workflow}
									fieldDefinitions={formData.smartProgram.fieldDefinitions}
									description={formData.smartProgram.description}
									generationPrompt={formData.smartProgram.generationPrompt}
									previewHtml={formData.smartProgram.previewHtml}
								/>
							)}
							<Grid item xs={12}>
								<FieldDefinitionsEditor
									fields={formData.smartProgram?.fieldDefinitions || []}
									onChange={(fields) =>
										setFormData({
											...formData,
											smartProgram: {
												...formData.smartProgram,
												fieldDefinitions: fields,
											},
										})
									}
								/>
							</Grid>
						</>
					)}

					{/* Treatment Config Templates (Weekly Calendar only) */}
					{(!formData.programType ||
						formData.programType === "WEEKLY_CALENDAR") && (
						<>
							{/* Treatment Config Templates */}
							<Grid item xs={12}>
								<Box
									sx={{
										border: "1px solid #e0e0e0",
										borderRadius: 1,
										p: 2,
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 2,
										}}
									>
										<Typography variant="subtitle1">
											Treatment Config Templates
										</Typography>
										<Button
											variant="outlined"
											size="small"
											onClick={() => {
												if (showTemplateForm) {
													// Cancel - close form and reset
													setShowTemplateForm(false);
													setEditingTemplateIndex(null);
													setTemplateData({
														name: "",
														default: false,
														segment: 0,
														range: "",
														duration: 0,
														periodLength: 1,
														weeklyVisitsPerPeriod: 0,
														cmVisitScheduleOptionIds: "",
														rewardEngineId: "",
														visitsPerPeriod: 0,
														minHoursBetweenVisits: 0,
														cmVisitSchedule: {
															cmVisitScheduleOptions: 0,
															cmVisitScheduleOptionsDays: "",
															cmVisitScheduleOptionsDaysDisplay: "",
															cutDays: [],
														},
														behavior: "",
														disenrollmentDaysThreshold: undefined,
														allowCustomProviderSite: false,
													});
												} else {
													// Open form in create mode
													setEditingTemplateIndex(null);
													setShowTemplateForm(true);
												}
											}}
										>
											{showTemplateForm ? "Cancel" : "Add Template"}
										</Button>
									</Box>

									{/* Show existing templates */}
									{formData.treatmentConfigOptions?.treatmentConfigTemplates &&
										formData.treatmentConfigOptions.treatmentConfigTemplates
											.length > 0 && (
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													display="block"
													sx={{ mb: 1 }}
												>
													Existing Templates:
												</Typography>
												<Box
													sx={{
														display: "flex",
														flexWrap: "wrap",
														gap: 1,
													}}
												>
													{formData.treatmentConfigOptions.treatmentConfigTemplates.map(
														(template, index) => (
															<Box
																key={index}
																sx={{
																	display: "flex",
																	alignItems: "center",
																	border: "1px solid #e0e0e0",
																	borderRadius: 1,
																	px: 1,
																	py: 0.5,
																}}
															>
																<Typography
																	variant="body2"
																	sx={{ mr: 1, fontWeight: 500 }}
																>
																	{template.name || `Template ${index + 1}`}
																</Typography>
																<IconButton
																	size="small"
																	onClick={() => editTemplate(index)}
																	title="Edit template"
																	sx={{ p: 0.5 }}
																>
																	<EditIcon fontSize="small" />
																</IconButton>
																<IconButton
																	size="small"
																	onClick={() => removeTemplate(index)}
																	title="Delete template"
																	sx={{ p: 0.5 }}
																	color="error"
																>
																	<DeleteIcon fontSize="small" />
																</IconButton>
															</Box>
														),
													)}
												</Box>
											</Box>
										)}

									{/* Template creation form */}
									{showTemplateForm && (
										<Card variant="outlined" sx={{ p: 2 }}>
											<Typography variant="subtitle2" sx={{ mb: 2 }}>
												{editingTemplateIndex !== null
													? "Edit Template"
													: "Create New Template"}
											</Typography>

											<Grid container spacing={2}>
												<Grid item xs={12}>
													{/* Use the reusable TreatmentConfigFields component */}
													<TreatmentConfigFields
														config={templateData}
														onChange={(updatedConfig) =>
															setTemplateData(updatedConfig)
														}
														options={{
															behaviors: events || [],
															durations:
																formData.treatmentConfigOptions?.duration?.map(
																	String,
																) || [],
															visitsPerPeriod:
																formData.treatmentConfigOptions?.visitsPerPeriod?.map(
																	String,
																) || [],
															minHoursBetweenVisits:
																formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
																	String,
																) || [],
															cmVisitScheduleOptions:
																formData.treatmentConfigOptions
																	?.cmVisitScheduleOptions || [],
															rewardEngineOptions:
																rewardEngines
																	?.filter((engine) => engine._id)
																	.map((engine) => ({
																		id: engine._id as string,
																		name: `${engine.eventType} - ${engine.event}`,
																	})) || [],
															cups: cupsData?.body || [],
														}}
														showDefaultCheckbox={true}
														showAllowCustomProviderSite={true}
													/>
												</Grid>

												{/* Additional Treatment Configs Section */}
												<Grid item xs={12}>
													<Box
														sx={{
															border: "1px solid #e0e0e0",
															borderRadius: 1,
															p: 2,
															mt: 2,
														}}
													>
														<Box
															sx={{
																display: "flex",
																justifyContent: "space-between",
																alignItems: "center",
																mb: 2,
															}}
														>
															<Typography variant="subtitle2">
																Additional Activities
															</Typography>
															<Typography
																variant="caption"
																color="text.secondary"
															>
																Add more activities beyond the preferred one
															</Typography>
														</Box>

														{/* List of Additional Configs */}
														{templateData.additionalTreatmentConfigs &&
															templateData.additionalTreatmentConfigs.length >
																0 && (
																<Box sx={{ mb: 2 }}>
																	{templateData.additionalTreatmentConfigs.map(
																		(config, index) => (
																			<Paper
																				key={index}
																				sx={{
																					p: 2,
																					mb: 1,
																					display: "flex",
																					justifyContent: "space-between",
																					alignItems: "center",
																				}}
																			>
																				<Box>
																					<Typography
																						variant="body2"
																						fontWeight="bold"
																					>
																						{config.name}
																					</Typography>
																					<Typography
																						variant="caption"
																						color="text.secondary"
																					>
																						Segment: {config.segment}, Duration:{" "}
																						{config.duration}, Visits/Period:{" "}
																						{config.visitsPerPeriod}
																					</Typography>
																				</Box>
																				<Box>
																					<IconButton
																						size="small"
																						onClick={() => {
																							// Remove this additional config
																							const updatedConfigs = [
																								...(templateData.additionalTreatmentConfigs ||
																									[]),
																							];
																							updatedConfigs.splice(index, 1);
																							setTemplateData({
																								...templateData,
																								additionalTreatmentConfigs:
																									updatedConfigs,
																							});
																						}}
																						color="error"
																					>
																						<DeleteIcon />
																					</IconButton>
																				</Box>
																			</Paper>
																		),
																	)}
																</Box>
															)}

														{/* Add Additional Config Form */}
														<Button
															variant="outlined"
															size="small"
															startIcon={<AddIcon />}
															onClick={() => {
																// Create a simplified config based on current template
																const newAdditionalConfig: TreatmentConfigTemplate =
																	{
																		name: `${templateData.name} - Additional Activity ${(templateData.additionalTreatmentConfigs?.length || 0) + 1}`,
																		default: false,
																		segment: templateData.segment,
																		range: templateData.range,
																		duration: templateData.duration,
																		periodLength: templateData.periodLength,
																		weeklyVisitsPerPeriod:
																			templateData.weeklyVisitsPerPeriod,
																		cmVisitScheduleOptionIds:
																			templateData.cmVisitScheduleOptionIds,
																		rewardEngineId: templateData.rewardEngineId,
																		visitsPerPeriod:
																			templateData.visitsPerPeriod,
																		minHoursBetweenVisits:
																			templateData.minHoursBetweenVisits,
																		cmVisitSchedule: {
																			...templateData.cmVisitSchedule,
																		},
																		behavior: "", // Empty behavior to be selected
																		disenrollmentDaysThreshold:
																			templateData.disenrollmentDaysThreshold,
																		allowCustomProviderSite:
																			templateData.allowCustomProviderSite,
																		additionalTreatmentConfigs: [], // Nested configs not supported
																	};

																setTemplateData({
																	...templateData,
																	additionalTreatmentConfigs: [
																		...(templateData.additionalTreatmentConfigs ||
																			[]),
																		newAdditionalConfig,
																	],
																});
															}}
														>
															Add Additional Activity
														</Button>

														{/* Edit forms for each additional config */}
														{templateData.additionalTreatmentConfigs &&
															templateData.additionalTreatmentConfigs.length >
																0 && (
																<Box sx={{ mt: 2 }}>
																	{templateData.additionalTreatmentConfigs.map(
																		(config, index) => {
																			const updateConfig = (
																				updates: Partial<TreatmentConfigTemplate>,
																			) => {
																				const updatedConfigs = [
																					...(templateData.additionalTreatmentConfigs ||
																						[]),
																				];
																				updatedConfigs[index] = {
																					...updatedConfigs[index],
																					...updates,
																				};
																				setTemplateData({
																					...templateData,
																					additionalTreatmentConfigs:
																						updatedConfigs,
																				});
																			};

																			return (
																				<Card
																					key={index}
																					variant="outlined"
																					sx={{
																						p: 2,
																						mb: 2,
																						bgcolor: "grey.50",
																					}}
																				>
																					<Box
																						sx={{
																							display: "flex",
																							justifyContent: "space-between",
																							alignItems: "center",
																							mb: 2,
																						}}
																					>
																						<Typography variant="subtitle2">
																							Additional Activity {index + 1}
																						</Typography>
																						<IconButton
																							size="small"
																							onClick={() => {
																								const updatedConfigs = [
																									...(templateData.additionalTreatmentConfigs ||
																										[]),
																								];
																								updatedConfigs.splice(index, 1);
																								setTemplateData({
																									...templateData,
																									additionalTreatmentConfigs:
																										updatedConfigs,
																								});
																							}}
																							color="error"
																						>
																							<DeleteIcon />
																						</IconButton>
																					</Box>

																					{/* Use the reusable TreatmentConfigFields component */}
																					<TreatmentConfigFields
																						config={config}
																						onChange={(updatedConfig) =>
																							updateConfig(updatedConfig)
																						}
																						options={{
																							behaviors: events || [],
																							durations:
																								formData.treatmentConfigOptions?.duration?.map(
																									String,
																								) || [],
																							visitsPerPeriod:
																								formData.treatmentConfigOptions?.visitsPerPeriod?.map(
																									String,
																								) || [],
																							minHoursBetweenVisits:
																								formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
																									String,
																								) || [],
																							cmVisitScheduleOptions:
																								formData.treatmentConfigOptions
																									?.cmVisitScheduleOptions ||
																								[],
																							rewardEngineOptions:
																								rewardEngines
																									?.filter(
																										(engine) => engine._id,
																									)
																									.map((engine) => ({
																										id: engine._id as string,
																										name: `${engine.eventType} - ${engine.event}`,
																									})) || [],
																							cups: cupsData?.body || [],
																						}}
																						showDefaultCheckbox={false}
																						showAllowCustomProviderSite={true}
																						isAdditionalConfig={true}
																					/>
																				</Card>
																			);
																		},
																	)}
																</Box>
															)}
													</Box>
												</Grid>

												<Grid item xs={12}>
													<Button
														variant="contained"
														onClick={addTemplate}
														disabled={!templateData.name.trim()}
													>
														{editingTemplateIndex !== null
															? "Update Template"
															: "Add Template"}
													</Button>
												</Grid>
											</Grid>
										</Card>
									)}
								</Box>
							</Grid>

							{/* Visit schedules, and the periods each check counts. */}
							<Grid item xs={12}>
								<Box
									sx={{
										border: "1px solid #e0e0e0",
										borderRadius: 1,
										p: 2,
									}}
								>
									<VisitScheduleEditor
										options={
											formData.treatmentConfigOptions?.cmVisitScheduleOptions ??
											[]
										}
										onChange={(cmVisitScheduleOptions) =>
											setFormData((previous) => ({
												...previous,
												treatmentConfigOptions: {
													...previous.treatmentConfigOptions,
													cmVisitScheduleOptions,
												},
											}))
										}
									/>
								</Box>
							</Grid>

							{/* Visits Per Period */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Visits Per Period
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newVisitPeriod}
										onChange={(e) => setNewVisitPeriod(e.target.value)}
										placeholder="e.g., weekly, monthly"
									/>
									<IconButton
										onClick={() =>
											addArrayItem(
												"visitsPerPeriod",
												newVisitPeriod,
												setNewVisitPeriod,
											)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.visitsPerPeriod?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() =>
													removeArrayItem("visitsPerPeriod", index)
												}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Min Hours Between Visits */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Min Hours Between Visits
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newMinHours}
										onChange={(e) => setNewMinHours(e.target.value)}
										placeholder="e.g., 24, 48"
									/>
									<IconButton
										onClick={() =>
											addArrayItem(
												"minHoursBetweenVisits",
												newMinHours,
												setNewMinHours,
											)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() =>
													removeArrayItem("minHoursBetweenVisits", index)
												}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Duration */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Duration
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newDuration}
										onChange={(e) => setNewDuration(e.target.value)}
										placeholder="e.g., 30 days, 60 days"
									/>
									<IconButton
										onClick={() =>
											addArrayItem("duration", newDuration, setNewDuration)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.duration?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() => removeArrayItem("duration", index)}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Behavior */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Behavior (Events)
								</Typography>
								<BehaviorSelector
									label="Treatment Plan Behaviors"
									placeholder="Choose events that represent behaviors"
									helperText="Select from available events - each event represents a behavior type that will be available in treatment configurations"
									value={formData.treatmentConfigOptions?.behavior || []}
									onChange={(behaviorIds) => {
										if (formData.treatmentConfigOptions) {
											setFormData({
												...formData,
												treatmentConfigOptions: {
													...formData.treatmentConfigOptions,
													behavior: behaviorIds,
												},
											});
										}
									}}
									events={events}
									loading={eventsLoading}
								/>
							</Grid>

							{/* Custom Plan Allowed */}
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.customPlanAllowed || false}
											onChange={(e) =>
												setFormData({
													...formData,
													customPlanAllowed: e.target.checked,
												})
											}
										/>
									}
									label="Allow Custom Plans"
									sx={{ mt: 1 }}
								/>
								<Typography
									variant="caption"
									display="block"
									color="text.secondary"
								>
									When enabled, users can create custom treatment configurations
									for this plan
								</Typography>
							</Grid>

							{/* SIMS-7151/7159: plan-level segment editor (FR-3, FR-4, FR-6) */}
							<Grid item xs={12}>
								<SegmentEditor
									segments={formData.segments || []}
									onChange={(segments: TreatmentSegment[]) =>
										setFormData({ ...formData, segments })
									}
									rewardEngineOptions={rewardEngineOptions}
									behaviors={events || []}
									cmVisitScheduleOptions={
										formData.treatmentConfigOptions?.cmVisitScheduleOptions ||
										[]
									}
									templateDefaults={segmentTemplateDefaults}
								/>
							</Grid>
						</>
					)}

					<Grid item xs={12}>
						<Box sx={{ display: "flex", gap: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmit}
								disabled={createMutation.isPending || updateMutation.isPending}
							>
								{createMutation.isPending || updateMutation.isPending
									? "Saving..."
									: isEditMode
										? "Update Treatment Plan"
										: "Create Treatment Plan"}
							</Button>
							<Button variant="outlined" onClick={onClose}>
								Cancel
							</Button>
						</Box>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};

export default TreatmentPlanForm;
